/** Per-boss signature movement style (docs/02 "each boss moves differently"). */
export type BossMovement = 'orbit' | 'strafe' | 'charge' | 'hover' | 'prowl';

/** Movement tuning (px, px/s, rad/s, s) — shared by every DSL boss. */
const M = {
  orbit: { rate: 0.25, rx: 170, ry: 120, yFreq: 1.3, follow: 0.8 },
  strafe: { rate: 0.7, rx: 320, mirrorY: 0.35, followX: 1.2, followY: 0.6 },
  hover: { rate: 0.6, rx: 70, ry: 50, yFreq: 0.8, follow: 1.0 },
  prowl: { rate: 0.5, standoff: 360, follow: 0.9 },
  charge: { windup: 0.5, speed: 520, time: 0.55, cooldown: 3.2, firstAt: 2.5, home: 0.5 },
  jink: { firstAt: 3.0, base: 2.4, step: 0.55, decay: 4.5 },
} as const;

interface Body {
  x: number;
  y: number;
}

/**
 * Signature movement + the periodic sidestep jink (docs/12 G9). Pure state
 * machine: the boss hands in its position and gets it moved one step.
 */
export class BossMotion {
  /** Charge wind-up remaining — the body tints red while > 0 (telegraph) */
  windup = 0;
  private angle = 0;
  private jinkTimer: number = M.jink.firstAt;
  private jinkDir = 1;
  private jinkVel = 0;
  private chargeTimer: number = M.charge.firstAt;
  private chargeDirX = 0;
  private chargeDirY = 0;
  private charging = 0;

  constructor(
    private readonly kind: BossMovement,
    private readonly level: number,
    private readonly jinkSpeed: number,
  ) {}

  step(
    b: Body, homeX: number, homeY: number, dt: number, px: number, py: number, s: number,
  ): void {
    switch (this.kind) {
      case 'orbit': {
        const o = M.orbit;
        this.angle += o.rate * s * dt;
        const tx = homeX + Math.cos(this.angle) * o.rx;
        const ty = homeY + Math.sin(this.angle * o.yFreq) * o.ry;
        b.x += (tx - b.x) * Math.min(1, o.follow * s * dt);
        b.y += (ty - b.y) * Math.min(1, o.follow * s * dt);
        break;
      }
      case 'strafe': {
        // Horizontal sweep, loosely mirroring the player's Y
        const o = M.strafe;
        this.angle += o.rate * s * dt;
        const tx = homeX + Math.sin(this.angle) * o.rx;
        const ty = homeY + (py - homeY) * o.mirrorY;
        b.x += (tx - b.x) * Math.min(1, o.followX * s * dt);
        b.y += (ty - b.y) * Math.min(1, o.followY * s * dt);
        break;
      }
      case 'hover': {
        // Anchored sentry — stays near home with a slow tight bob
        const o = M.hover;
        this.angle += o.rate * s * dt;
        const tx = homeX + Math.cos(this.angle) * o.rx;
        const ty = homeY + Math.sin(this.angle * o.yFreq) * o.ry;
        b.x += (tx - b.x) * Math.min(1, o.follow * s * dt);
        b.y += (ty - b.y) * Math.min(1, o.follow * s * dt);
        break;
      }
      case 'prowl': {
        // Predator — slowly circles the player at a fixed standoff distance
        const o = M.prowl;
        this.angle += o.rate * s * dt;
        const tx = px + Math.cos(this.angle) * o.standoff;
        const ty = py + Math.sin(this.angle) * o.standoff;
        b.x += (tx - b.x) * Math.min(1, o.follow * s * dt);
        b.y += (ty - b.y) * Math.min(1, o.follow * s * dt);
        break;
      }
      case 'charge':
        this.charge(b, homeX, homeY, dt, px, py, s);
        break;
    }
  }

  /** Hover → wind-up (red tint telegraph) → lunge at the player. */
  private charge(
    b: Body, homeX: number, homeY: number, dt: number, px: number, py: number, s: number,
  ): void {
    const c = M.charge;
    if (this.charging > 0) {
      this.charging -= dt;
      b.x += this.chargeDirX * c.speed * s * dt;
      b.y += this.chargeDirY * c.speed * s * dt;
    } else if (this.windup > 0) {
      this.windup -= dt;
      if (this.windup <= 0) {
        const d = Math.hypot(px - b.x, py - b.y) || 1;
        this.chargeDirX = (px - b.x) / d;
        this.chargeDirY = (py - b.y) / d;
        this.charging = c.time;
      }
    } else {
      this.chargeTimer -= dt;
      b.x += (homeX - b.x) * Math.min(1, c.home * dt);
      b.y += (homeY - b.y) * Math.min(1, c.home * dt);
      if (this.chargeTimer <= 0) {
        this.chargeTimer = c.cooldown / s;
        this.windup = c.windup;
      }
    }
  }

  /**
   * Non-charge bosses snap sideways every few seconds — the cycle length
   * is level-dependent so it never feels metronomic. Runs BEFORE the
   * signature (or bespoke) movement step, so custom movers keep it too.
   */
  jink(b: Body, dt: number, px: number, py: number): void {
    if (this.kind === 'charge') return;
    const j = M.jink;
    this.jinkTimer -= dt;
    if (this.jinkTimer <= 0) {
      this.jinkTimer = j.base + ((this.level * 7) % 5) * j.step;
      this.jinkDir = -this.jinkDir;
      this.jinkVel = this.jinkSpeed;
    }
    if (this.jinkVel > 1) {
      const d = Math.hypot(px - b.x, py - b.y) || 1;
      const perpX = -(py - b.y) / d;
      const perpY = (px - b.x) / d;
      b.x += perpX * this.jinkDir * this.jinkVel * dt;
      b.y += perpY * this.jinkDir * this.jinkVel * dt;
      this.jinkVel *= Math.exp(-j.decay * dt);
    }
  }
}
